import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bot, Download, Gavel, ShieldCheck, Workflow, Wrench } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Skeleton, cn, notifySaveFailed } from "@ncp/ui";

export interface AgentCatalogEntry {
  key: string; name: string; nameAr: string; category: "WorkflowAgent" | "Assistant" | "Service";
  purpose: string; purposeAr: string; inputs: string[]; outputs: string[]; humanDecision: string; proposalKinds: string[]; guardrails: string;
  tier?: string | null; modelId?: string | null; reviewerRole?: string | null; tools: string[]; workflowSteps: string[]; promptVersion: string;
  runs30d: number; proposals30d: number; acceptanceRate?: number | null; avgLatencyMs?: number | null; registered: boolean;
}

const categories: Array<AgentCatalogEntry["category"]> = ["WorkflowAgent", "Assistant", "Service"];

/**
 * "Is there a description of how each agent works?" — one card per agent: purpose, inputs, outputs, tools, the workflow steps that invoke it,
 * who approves, the guardrails, and the last 30 days of activity. Exportable as Markdown for the client's documentation set.
 */
export function AgentCatalogTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const catalog = useQuery({ queryKey: ["governance", "agent-catalog"], queryFn: () => api.get<AgentCatalogEntry[]>("/api/governance/agents/catalog") });
  const [filter, setFilter] = useState("");
  const [downloading, setDownloading] = useState(false);
  const rows = useMemo(() => (catalog.data ?? []).filter((a) => !filter || `${a.name} ${a.nameAr} ${a.purpose} ${a.tools.join(" ")} ${a.workflowSteps.join(" ")}`.toLowerCase().includes(filter.toLowerCase())), [catalog.data, filter]);

  const download = async () => {
    setDownloading(true);
    try {
      await api.download("/api/governance/agents/catalog/export", `ncp-agent-catalogue-${new Date().toISOString().slice(0, 10)}.md`);
    } catch (e) {
      notifySaveFailed(e, t("misc.error"));
    } finally {
      setDownloading(false);
    }
  };

  if (catalog.isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Input className="max-w-md" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("admin:governance.agentCatalog.filter")} />
        <p className="text-xs text-muted-foreground">{t("admin:governance.agentCatalog.count", { count: rows.length })}</p>
        <span className="flex-1" />
        <Button variant="outline" loading={downloading} onClick={() => void download()}><Download /> {t("admin:governance.agentCatalog.export")}</Button>
      </div>
      <p className="text-sm text-muted-foreground">{t("admin:governance.agentCatalog.hint")}</p>
      {categories.map((cat) => {
        const items = rows.filter((a) => a.category === cat);
        if (items.length === 0) return null;
        return (
          <section key={cat} className="space-y-3">
            <h3 className="flex items-center gap-2 text-base font-semibold">{cat === "WorkflowAgent" ? <Workflow className="size-4 text-aegold-700" /> : cat === "Assistant" ? <Bot className="size-4 text-aegold-700" /> : <Wrench className="size-4 text-aegold-700" />} {t(`admin:governance.agentCatalog.categories.${cat}`)} <Badge size="sm" variant="outline">{items.length}</Badge></h3>
            <div className="grid gap-4 xl:grid-cols-2">
              {items.map((a) => (
                <Card key={a.key} className={cn(!a.registered && "opacity-70")}>
                  <CardHeader className="pb-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base">{locale === "ar" ? a.nameAr : a.name} <span className="ms-1 font-mono text-[11px] font-normal text-muted-foreground">{a.key}</span></CardTitle>
                        <CardDescription className="mt-1 leading-relaxed">{locale === "ar" ? a.purposeAr : a.purpose}</CardDescription>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {a.tier ? <Badge size="sm" variant={a.tier === "Reasoning" ? "gold" : "outline"}>{a.tier}</Badge> : <Badge size="sm" variant="outline">{t("admin:governance.agentCatalog.deterministic")}</Badge>}
                        <Badge size="sm" variant="outline">v{a.promptVersion}</Badge>
                        {a.acceptanceRate != null ? <Badge size="sm" variant={a.acceptanceRate >= 80 ? "success" : a.acceptanceRate >= 60 ? "warning" : "danger"}>{a.acceptanceRate}% {t("admin:governance.agentCatalog.accepted")}</Badge> : null}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("admin:governance.agentCatalog.inputs")}</p>
                      <ul className="mt-1 list-disc space-y-0.5 ps-4 text-xs">{a.inputs.map((x) => <li key={x}>{x}</li>)}</ul>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("admin:governance.agentCatalog.outputs")}</p>
                      <ul className="mt-1 list-disc space-y-0.5 ps-4 text-xs">{a.outputs.map((x) => <li key={x}>{x}</li>)}</ul>
                    </div>
                    {a.workflowSteps.length ? (
                      <div className="md:col-span-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("admin:governance.agentCatalog.runsAt")}</p>
                        <div className="mt-1 flex flex-wrap gap-1">{a.workflowSteps.map((s) => <Badge key={s} size="sm" variant="outline"><Workflow /> {s}</Badge>)}</div>
                      </div>
                    ) : null}
                    {a.tools.length ? (
                      <div className="md:col-span-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("admin:governance.agentCatalog.tools")}</p>
                        <div className="mt-1 flex flex-wrap gap-1">{a.tools.map((s) => <span key={s} className="rounded bg-sand-100 px-1.5 py-0.5 font-mono text-[11px]">{s}</span>)}</div>
                      </div>
                    ) : null}
                    <div className="md:col-span-2 rounded-xl bg-sand-50 p-3 text-xs">
                      <p className="flex items-start gap-1.5"><Gavel className="mt-0.5 size-3.5 shrink-0 text-aegold-700" /><span><span className="font-semibold">{t("admin:governance.agentCatalog.humanDecision")}:</span> {a.humanDecision}{a.reviewerRole ? ` (${t(`roles.${a.reviewerRole}`, { defaultValue: a.reviewerRole })})` : ""}</span></p>
                      <p className="mt-1.5 flex items-start gap-1.5"><ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-aegold-700" /><span><span className="font-semibold">{t("admin:governance.agentCatalog.guardrails")}:</span> {a.guardrails}</span></p>
                    </div>
                    <p className="md:col-span-2 text-[11px] text-muted-foreground">{t("admin:governance.agentCatalog.last30", { runs: a.runs30d, proposals: a.proposals30d })}{a.avgLatencyMs != null ? ` · ${Math.round(a.avgLatencyMs)} ms` : ""}{a.modelId ? ` · ${a.modelId}` : ""}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
